import type { SessionInfo } from '@cm/protocol';
import { paneColor, paneName } from '../layout';
import { useActiveLayout, useStore } from '../store';
import { agentColor } from '../theme';
import { AgentMark } from './AgentMark';

interface Props {
  workspaceId: string;
  sessions: SessionInfo[];
}

/**
 * Barra dos panes recolhidos.
 *
 * Minimizar desmonta o terminal do cliente; ao restaurar, o daemon reenvia o
 * scrollback e a tela se repinta. Por isso o que passar do limite do ring
 * buffer (CM_SCROLLBACK_BYTES) nao volta -- o processo do agente continua
 * rodando normalmente, so o historico visivel e que tem teto.
 */
export function MinimizedStrip({ workspaceId, sessions }: Props) {
  const layout = useActiveLayout();
  const updateLayout = useStore((s) => s.updateLayout);
  const focus = useStore((s) => s.focus);
  const tokens = useStore((s) => s.theme.tokens);

  const byId = new Map(sessions.map((s) => [s.id, s]));
  const items = layout.minimized
    .map((id) => byId.get(id))
    .filter((s): s is SessionInfo => s !== undefined);

  if (items.length === 0) return null;

  return (
    <div className="minimized-strip">
      <span className="strip-label">recolhidos</span>
      {items.map((session) => {
        const name = paneName(layout, session.id, session.title);
        const accent = paneColor(layout, session.id) ?? agentColor(tokens, session.agent);
        return (
        <button
          key={session.id}
          className="strip-item"
          style={{ ['--agent-color' as string]: accent }}
          onClick={() => {
            updateLayout(workspaceId, (l) => ({
              ...l,
              minimized: l.minimized.filter((x) => x !== session.id),
            }));
            focus(session.id);
          }}
          title={`Restaurar ${name}`}
        >
          <AgentMark agent={session.agent} size={12} />
          <span className="strip-title">{name}</span>
          <span
            className="strip-status"
            style={{
              color: session.status === 'running' ? 'var(--ok)' : 'var(--danger)',
            }}
          >
            {session.status === 'running' ? '●' : '○'}
          </span>
        </button>
        );
      })}
    </div>
  );
}
